import React from "react";
import { Box, Sheet, Typography, IconButton } from "@mui/joy";
import { Outlet } from "@tanstack/react-router";
import LogoutIcon from "@mui/icons-material/Logout";
import roosterLogo from "../../assets/rooster_logo_no_text.png";
import s from "./Layout.module.scss";
import { useLogOut } from "../../shared/hook";

interface OnboardingUser {
  email: string;
  displayName: string;
  uuid: string;
}

const OnboardingLayout: React.FC = () => {
  const logout = useLogOut();

  // Get user from sessionStorage
  const getUserFromStorage = (): OnboardingUser | null => {
    try {
      const storedUser = sessionStorage.getItem("onboardingUser");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  };

  const user = getUserFromStorage();

  const handleLogout = async () => {
    await logout();
  };

  // Format user display name - remove "User" suffix and show full name
  const getDisplayName = (displayName: string) => {
    // Remove " User" suffix if it exists and return the clean name
    return displayName.replace(/ User$/, "");
  };

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        margin: 0,
        padding: 0,
        position: "relative"
      }}
    >
      {/* Always show the navbar for onboarding pages */}
      <Sheet
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3, md: 4 },
          py: 1.5,
          bgcolor: "background.body",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          color: "text.primary",
          width: "100vw",
          boxSizing: "border-box"
        }}
      >
        {/* Left side - Logo */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="img"
              src={roosterLogo}
              alt="Rooster"
              sx={{
                height: "32px",
                width: "auto"
              }}
            />
            <Typography
              level="title-lg"
              sx={{
                color: "text.primary",
                fontWeight: 600,
                fontSize: "1.25rem"
              }}
            >
              rooster
            </Typography>
          </Box>
        </Box>

        {/* Right side - User Profile and Logout */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          {/* User Name (only show if user is logged in) */}
          {user && (
            <Typography
              level="body-sm"
              sx={{
                color: "text.primary",
                fontWeight: 500
              }}
            >
              {getDisplayName(user.displayName)}
            </Typography>
          )}

          {/* Logout Icon - always visible for navigation back to login */}
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={handleLogout}
            sx={{
              color: "text.primary",
              "&:hover": {
                bgcolor: "background.level1"
              }
            }}
            title="Logout"
          >
            <LogoutIcon />
          </IconButton>
        </Box>
      </Sheet>
      <Box
        className={s.main}
        sx={{
          marginTop: "60px",
          minHeight: "calc(100vh - 60px)",
          width: "100vw",
          boxSizing: "border-box"
        }}
      >
        <React.Suspense>
          <Outlet />
        </React.Suspense>
      </Box>
    </Box>
  );
};

export default OnboardingLayout;
